import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Calendar, FileQuestion, Crown, Music, FileText, Image,
  Languages, Heart, X, Share2, Layers, Compass
} from 'lucide-react';
import { PremiumFeature } from './PremiumFeature';
import { PassageGuide } from './Tools/PassageGuide';
import { SermonScheduler } from './Tools/SermonScheduler';
import { StudyQuestions } from './Tools/StudyQuestions';
import { ServiceAssets } from './Tools/ServiceAssets';
import { SermonSummarizer } from './Tools/SermonSummarizer';
import { SermonImageGenerator } from './Tools/SermonImageGenerator';
import { SermonTranslator } from './Tools/SermonTranslator';
import { FamilyDevotional } from './Tools/FamilyDevotional';
import { SocialMediaAssets } from './Tools/SocialMediaAssets';
import { SocialCarouselBuilder } from './Tools/SocialCarouselBuilder';

type Tool =
  | 'passage-guide'
  | 'scheduler'
  | 'study-questions'
  | 'service-assets'
  | 'summarizer'
  | 'image-generator'
  | 'translator'
  | 'family-devotional'
  | 'social-media'
  | 'social-carousel';

interface ToolConfig {
  id: Tool;
  name: string;
  icon: React.ElementType;
  description: string;
  feature: string;
  comingSoon?: boolean;
}

interface Category {
  label: string;
  description: string;
  toolIds: Tool[];
}

const CATEGORIES: Category[] = [
  {
    label: 'Preparation',
    description: 'Plan your preaching calendar and build study resources',
    toolIds: ['passage-guide', 'scheduler', 'study-questions', 'service-assets'],
  },
  {
    label: 'Content & Publishing',
    description: 'Summarize, visualize, and share your sermons',
    toolIds: ['summarizer', 'image-generator', 'translator', 'social-media', 'social-carousel'],
  },
  {
    label: 'Ministry Resources',
    description: "Extend your sermon's reach into homes and families",
    toolIds: ['family-devotional'],
  },
];

const ALL_TOOLS: ToolConfig[] = [
  {
    id: 'passage-guide',
    name: 'Passage Guide',
    icon: Compass,
    description: 'A research brief for the passage you are preaching — word study, context, insights from your tradition, seed ideas, and title options.',
    feature: 'sermon_summarizer',
  },
  {
    id: 'scheduler',
    name: 'Sermon Scheduler',
    icon: Calendar,
    description: 'Plan your sermon calendar, coordinate with team members, and manage series scheduling.',
    feature: 'sermon_scheduler',
  },
  {
    id: 'study-questions',
    name: 'Study Questions',
    icon: FileQuestion,
    description: 'Generate tailored small-group study materials and discussion questions from your sermons.',
    feature: 'study_questions',
  },
  {
    id: 'service-assets',
    name: 'Service Assets',
    icon: Music,
    description: 'AI suggestions for worship songs, scripture readings, and congregational elements that align with your message.',
    feature: 'service_assets',
  },
  {
    id: 'summarizer',
    name: 'Sermon Study Guide',
    icon: FileText,
    description: 'Create a shareable study page with sermon summary, video, and discussion questions.',
    feature: 'sermon_summarizer',
  },
  {
    id: 'image-generator',
    name: 'Image Generator',
    icon: Image,
    description: 'Create beautiful sermon graphics and title slides with AI based on your sermon content.',
    feature: 'sermon_summarizer',
  },
  {
    id: 'translator',
    name: 'Sermon Translator',
    icon: Languages,
    description: 'Translate your sermons into multiple languages using AI while preserving theological accuracy.',
    feature: 'sermon_summarizer',
  },
  {
    id: 'family-devotional',
    name: 'Family Devotional',
    icon: Heart,
    description: 'Generate a published-quality family devotional — main devotion, memory verse, object lesson, age guide, and parental blessing.',
    feature: 'sermon_summarizer',
  },
  {
    id: 'social-media',
    name: 'Social Media Assets',
    icon: Share2,
    description: 'AI-generated post graphics and captions for Instagram, Facebook, and Stories in three brand styles.',
    feature: 'sermon_summarizer',
  },
  {
    id: 'social-carousel',
    name: 'Carousel Builder',
    icon: Layers,
    description: 'Claude reads your sermon and designs a complete branded carousel — hook, key points, scripture, and CTA — rendered as publication-ready PNGs.',
    feature: 'sermon_summarizer',
  },
];

export function Tools() {
  const location = useLocation();
  const toolContentRef = useRef<HTMLDivElement>(null);
  const [activeTool, setActiveTool] = useState<Tool | null>(null);
  const [preselectedSermonId, setPreselectedSermonId] = useState<string | undefined>(undefined);

  // Auto-open tool and pre-select sermon when navigated from editor
  useEffect(() => {
    const state = location.state as { toolId?: Tool; sermonId?: string } | null;
    if (state?.toolId) {
      setActiveTool(state.toolId);
      setPreselectedSermonId(state.sermonId);
      setTimeout(() => {
        toolContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    }
  }, [location.state]);

  const handleSelectTool = (tool: ToolConfig) => {
    if (tool.comingSoon) return;
    if (activeTool === tool.id) {
      setActiveTool(null);
      return;
    }
    setActiveTool(tool.id);
    setPreselectedSermonId(undefined);
    setTimeout(() => {
      toolContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const activeCfg = ALL_TOOLS.find(t => t.id === activeTool) ?? null;

  return (
    <div className="p-4 md:p-8 pb-20 md:pb-8">

      {/* Page header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-midnight dark:text-soft-white">Tools</h1>
        <p className="text-sm text-slate dark:text-soft-white/50 mt-1">
          AI-powered tools to prepare, create, and share your ministry content.
        </p>
      </div>

      {/* Category sections */}
      <div className="space-y-8">
        {CATEGORIES.map(cat => {
          const catTools = ALL_TOOLS.filter(t => cat.toolIds.includes(t.id));
          return (
            <div key={cat.label}>
              {/* Category header */}
              <div className="mb-3">
                <h2 className="text-xs font-bold uppercase tracking-widest text-slate dark:text-soft-white/40">
                  {cat.label}
                </h2>
              </div>

              {/* 3-column tool grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {catTools.map(tool => {
                  const isActive = activeTool === tool.id;
                  const Icon = tool.icon;

                  return (
                    <div
                      key={tool.id}
                      onClick={() => handleSelectTool(tool)}
                      className={`
                        group relative rounded-xl border p-4 transition-all duration-150
                        ${tool.comingSoon
                          ? 'opacity-60 cursor-not-allowed bg-white dark:bg-charcoal border-sand/20 dark:border-slate/20'
                          : isActive
                            ? 'cursor-pointer bg-electric/5 dark:bg-electric/10 border-electric shadow-sm'
                            : 'cursor-pointer bg-white dark:bg-charcoal border-sand/20 dark:border-slate/20 hover:border-electric/30 dark:hover:border-electric/30 hover:shadow-md hover:bg-sand/20 dark:hover:bg-slate/10'
                        }
                      `}
                    >
                      <div className="flex items-start gap-3">
                        {/* Icon badge */}
                        <div className={`
                          w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors
                          ${isActive
                            ? 'bg-electric/15 dark:bg-electric/20'
                            : 'bg-electric/10 dark:bg-electric/10 group-hover:bg-electric/15'}
                        `}>
                          <Icon className="h-5 w-5 text-electric" />
                        </div>

                        {/* Text */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                            <span className="text-sm font-semibold text-midnight dark:text-soft-white leading-snug">
                              {tool.name}
                            </span>
                            {tool.comingSoon ? (
                              <span className="inline-flex items-center px-1.5 py-0.5 bg-slate/10 dark:bg-slate/20 text-slate dark:text-soft-white/50 text-[10px] font-semibold rounded-full">
                                Soon
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-1.5 py-0.5 bg-gold/10 text-gold text-[10px] font-semibold rounded-full">
                                <Crown className="h-2.5 w-2.5 mr-0.5" />
                                Premium
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate dark:text-soft-white/50 line-clamp-2 leading-relaxed">
                            {tool.description}
                          </p>
                        </div>
                      </div>

                      {/* Active indicator */}
                      {isActive && (
                        <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-electric/20">
                          <span className="w-1.5 h-1.5 rounded-full bg-electric inline-block" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-electric">
                            Open below
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Active tool panel */}
      {activeCfg && (
        <div
          ref={toolContentRef}
          className="mt-8 rounded-xl border border-sand/20 dark:border-slate/20 overflow-hidden shadow-sm"
        >
          {/* Panel header */}
          <div className="flex items-center justify-between px-5 py-3.5 bg-sand/30 dark:bg-slate/20 border-b border-sand/20 dark:border-slate/20">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-lg bg-electric/10 dark:bg-electric/15 flex items-center justify-center">
                <activeCfg.icon className="h-4 w-4 text-electric" />
              </div>
              <span className="text-sm font-semibold text-midnight dark:text-soft-white">
                {activeCfg.name}
              </span>
              {preselectedSermonId && (
                <span className="text-xs text-electric/60 font-normal">· sermon pre-selected</span>
              )}
            </div>
            <button
              onClick={() => setActiveTool(null)}
              className="p-1.5 text-slate dark:text-soft-white/50 hover:text-midnight dark:hover:text-soft-white rounded-lg hover:bg-sand/40 dark:hover:bg-slate/30 transition-colors"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Tool content */}
          <div className="p-6 bg-white dark:bg-charcoal">
            <PremiumFeature feature={activeCfg.feature}>
              <>
                {activeTool === 'passage-guide' && <PassageGuide mode="page" defaultSermonId={preselectedSermonId} />}
                {activeTool === 'scheduler' && <SermonScheduler />}
                {activeTool === 'study-questions' && <StudyQuestions defaultSermonId={preselectedSermonId} />}
                {activeTool === 'service-assets' && <ServiceAssets defaultSermonId={preselectedSermonId} />}
                {activeTool === 'summarizer' && <SermonSummarizer defaultSermonId={preselectedSermonId} />}
                {activeTool === 'image-generator' && <SermonImageGenerator defaultSermonId={preselectedSermonId} />}
                {activeTool === 'translator' && <SermonTranslator defaultSermonId={preselectedSermonId} />}
                {activeTool === 'family-devotional' && <FamilyDevotional defaultSermonId={preselectedSermonId} />}
                {activeTool === 'social-media' && <SocialMediaAssets defaultSermonId={preselectedSermonId} />}
                {activeTool === 'social-carousel' && <SocialCarouselBuilder defaultSermonId={preselectedSermonId} />}
              </>
            </PremiumFeature>
          </div>
        </div>
      )}
    </div>
  );
}
